/**
 * VCM Option Maps — numeric ID to label mappings
 *
 * Sourced from DCM Fields & Formats spreadsheet. These maps translate
 * the numeric option IDs used in the GAB backend to human-readable labels.
 * When the real backend is connected, field values will use these IDs.
 */

/** Gender options — ClientOption_GenderOptionId */
export const GENDER_OPTIONS: Record<number, string> = {
  28: 'Female',
  29: 'Male',
  707: 'Not Listed',
  32: 'Other or Unknown',
};

/** Preferred language options — ClientOption_PreferredLanguageOptionId */
export const LANGUAGE_OPTIONS: Record<number, string> = {
  36: 'Spanish',
  37: 'Chinese',
  38: 'French',
  39: 'German',
  40: 'Tagalog',
  41: 'Vietnamese',
  42: 'Arabic',
  43: 'Korean',
  44: 'Russian',
  45: 'American Sign Language',
  46: 'Other',
  47: 'Urdu',
};

/** Prior living situation — ClientOption_PriorLivingSituationBestDescribedAs */
export const PRIOR_LIVING_OPTIONS: Record<number, string> = {
  664: 'A residence that is OWNED/CO-OWNED by applicant',
  665: 'Applicant RENTED',
  666: 'A residence belonging to FAMILY OR FRIENDS',
  667: 'PUBLIC HOUSING',
  668: 'MOBILE HOME (Manufactured home)',
  669: 'LONG-TERM CARE RESIDENCE (e.g., nursing home, assisted living)',
  670: 'HOTEL/MOTEL',
  671: 'UNSHELTERED (e.g., homeless)',
  673: 'OTHER',
};

/** Current residence — ClientOption_CurrentResidenceBestDescribedAs */
export const CURRENT_RESIDENCE_OPTIONS: Record<number, string> = {
  674: 'A residence that is OWNED/CO-OWNED by the applicant',
  675: 'Applicant RENTS',
  676: 'A residence belonging to FAMILY OR FRIENDS',
  677: 'TRANSITIONAL HOUSING (e.g., group home, community shelter)',
  678: 'PUBLIC HOUSING',
  679: 'MOBILE HOME (Manufactured home)',
  680: 'LONG-TERM CARE RESIDENCE (e.g., nursing home, assisted living)',
  681: 'HOTEL/MOTEL',
  682: 'UNSHELTERED (e.g., homeless)',
  684: 'OTHER',
  701: 'SAME',
  702: 'Emergency Shelter',
};

/** Service needs category — ClientOption_ServiceNeedsCategory */
export const SERVICE_NEEDS_OPTIONS: Record<number, string> = {
  638: 'Access and Functional Needs',
  639: 'Adult Education and Job Training',
  640: 'Benefits Navigation',
  641: 'Children and Youth Services',
  642: 'Clothing',
  643: 'Domestic Animal Assistance',
  644: 'Emotional and Spiritual Care',
  645: 'Employment',
  646: 'VA Benefits Advocacy',
  647: 'Financial Services',
  648: 'Food and Nutrition',
  649: 'Funeral Assistance',
  650: 'Household Furniture',
  651: 'Household Large Appliances',
  652: 'Household Storage',
  653: 'Housing – Permanent',
  654: 'Housing – Permanent',
  655: 'Housing – Temporary',
  656: 'Legal Assistance',
  657: 'Healthcare Navigation',
  658: 'Peer Support Services',
  659: 'Mental Health Services',
  660: 'Homeless Prevention',
  661: 'Peer Support Services',
  662: 'Transportation',
  663: 'Physical Rehabilitation',
  1756: 'Healthcare Navigation',
  1757: 'Community Connection',
  1758: 'Personal assistance (paperwork, scheduling, reminders)',
  1894: 'Property Materials',
  1895: 'Other',
  1897: 'Household Goods',
};

/** Client connect status — ClientOption_ClientConnectStatus */
export const CLIENT_CONNECT_STATUS_OPTIONS: Record<number, string> = {
  633: 'Requesting Follow Up',
  634: 'All Needs Met',
  635: 'Duplicate',
  636: 'Ready for Discharge',
};

/** Household member condition — HouseholdOption_ClientCondition */
export const HOUSEHOLD_CONDITION_OPTIONS: Record<number, string> = {
  552: 'Not Affected',
  553: 'Injured',
  554: 'Hospitalized',
  555: 'Missing',
  556: 'Deceased',
};

/** Household relationship — HouseholdOption_RelationshipHousehold */
export const HOUSEHOLD_RELATIONSHIP_OPTIONS: Record<number, string> = {
  532: 'Self',
  533: 'Spouse/Partner',
  534: 'Parent',
  535: 'Child',
  536: 'Sibling',
  537: 'Caregiver',
  538: 'Adult - Relative',
  539: 'Adult - Non-relative',
  1846: 'Minor - Relative',
  1847: 'Minor - Non-relative',
};

/** Shelter service needs — HouseholdOption_RequestingShelterServices */
export const SHELTER_SERVICE_OPTIONS: Record<number, string> = {
  1287: 'Health Services',
  1288: 'Mental Health Services',
  1289: 'Emotional and Spiritual Care',
  1290: 'Disability Accommodation',
  1291: 'Family Reunification',
  1292: 'Child Care',
};

/** Race — HouseholdOption_RaceHousehold */
export const RACE_OPTIONS_MAP: Record<number, string> = {
  1841: 'American Indian or Alaska Native',
  1842: 'Asian',
  1843: 'Black or African American',
  1844: 'Native Hawaiian or Other Pacific Islander',
  1845: 'White',
};

/** Ethnicity — HouseholdOption_EthnicityHousehold */
export const ETHNICITY_OPTIONS_MAP: Record<number, string> = {
  1839: 'Hispanic or Latino',
  1840: 'Not Hispanic or Latino',
};

/** Goal status — GoalOption_GoalStatus */
export const GOAL_STATUS_OPTIONS: Record<number, string> = {
  818: 'Goal Met',
  819: 'Goal Partially Met',
  820: 'Goal Unmet',
  821: 'Goal Abandoned',
  822: 'No Resources to Meet Goal',
  1863: 'In Progress',
};

// ---------------------------------------------------------------------------
// Backward-compatibility alias — existing TSX files import RECOVERY_NEEDS_OPTIONS
// ---------------------------------------------------------------------------

/** @deprecated Use SERVICE_NEEDS_OPTIONS */
export const RECOVERY_NEEDS_OPTIONS = SERVICE_NEEDS_OPTIONS;

/**
 * Utility: get the label for an option ID from any option map.
 * Returns the ID as a string if the label is not found.
 */
export function getOptionLabel(map: Record<number, string>, id: number | null | undefined): string {
  if (id == null) return '';
  return map[id] ?? String(id);
}

/**
 * Utility: get all options from a map as an array of { value, label } pairs.
 * Useful for populating select dropdowns.
 */
export function getOptionsArray(map: Record<number, string>): { value: number; label: string }[] {
  return Object.entries(map).map(([k, v]) => ({ value: Number(k), label: v }));
}
